// 라이브러리
import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";

// 공통 컴포넌트
import RouteErrorFallback from "../components/common/route-error-fallback";

// 사용자 정의 라이브러리
import { useAuth } from "../core/auth";
import { bossRoutes } from "./boss-routes";
import { adminRoutes } from "./admin-routes";
import { missionAdminRoutes } from "./mission-admin-routes";
import { brandRoutes } from "./brand-routes";
import { isWeb } from "../utils/user-agent";

// 홈 탭 라우트
import Home from "../Routes/home/page";
import OnboardingNotificationInfo from "../Routes/onboarding/notification/info/page";
import OnboardingNotificationRequest from "../Routes/onboarding/notification/request/page";
import OnboardingWelcome from "../Routes/onboarding/welcome/page";
import Search from "../Routes/search/page";
import Location from "../Routes/location/page";
import Like from "../Routes/like/page";
import Frequency from "../Routes/frequency/page";

// 스토어 라우트
import Store from "../Routes/store/page";
import StoreReview from "../Routes/store/review/page";
import Product from "../Routes/product/page";
import Pack from "../Routes/pack/page";

// 결제 라우트
import CheckoutLiveOrder from "../Routes/live-order/checkout/page";
import CheckoutPreOrderIntro from "../Routes/pre-order/intro/page";
import CheckoutPreOrder from "../Routes/pre-order/checkout/page";
import NiceResult from "../Routes/nice-result/page";
import TossWidgetCheckoutPage from "../Routes/payment/toss-widget-checkout/page";
import TossPaymentReturnPage from "../Routes/payment/toss-return/page";

// 찐후기 탭 라우트
import Explore from "../Routes/explore/page";

// 주문내역 탭 라우트
import Order from "../Routes/order/page";
import OrderDetail from "../Routes/order/detail/page";
import OrderReview from "../Routes/order/review/page";
import OrderWritableReview from "../Routes/order/writable-review/page";
import OrderReviewComplete from "../Routes/order/review/complete/page";

// 알림 라우트
import NotificationSettings from "../Routes/my/settings/notification-settings/page";

// 마이페이지 탭 라우트
import My from "../Routes/my/page";
import Review from "../Routes/my/review/page";
import Coupon from "../Routes/my/coupon/page";
import CouponRegister from "../Routes/my/coupon/register/page";
import CouponRegisterComplete from "../Routes/my/coupon/register/complete/page";
import Wallet from "../Routes/my/wallet/page";
import InviteIntro from "../Routes/my/invite/intro/page";
import InviteRegister from "../Routes/my/invite/register/page";
import InviteRegisterComplete from "../Routes/my/invite/register/complete/page";
import Policy from "../Routes/my/policy/page";
import CouponPolicy from "../Routes/my/policy/coupon/page";
import Contact from "../Routes/my/contact/page";
import CustomerService from "../Routes/my/customer-service/page";
import Settings from "../Routes/my/settings/page";

// 인증 라우트
import Login from "../Routes/login/page";
import KakaoRedirectHandler from "../Routes/login/kakao-redirect/page";
import SignUp from "../Routes/signup/page";
import SignOut from "../Routes/sign-out/page";
import ChangePassword from "../Routes/change-password/page";
import PasswordReset from "../Routes/password-reset/page";
import PhoneNumberAuthPage from "../Routes/phone-number-auth/page";
import PhoneNumberAuthCompletedPage from "../Routes/phone-number-auth/completed/page";

import OnboardingLocation from "../Routes/onboarding/location/page";

import SnsPromotion from "../Routes/event/sns-promotion/page";
import ManagerLogin from "../Routes/manager/login/page";
import ManagerHome from "../Routes/manager/home/page";
import OnboardingStart from "../Routes/onboarding/start/page";
import OnboardingPrice from "../Routes/onboarding/price/page";
import OnboardingFriends from "../Routes/onboarding/friends/page";
import UserGuide from "../Routes/user-guide/page";
import Welcome from "../Routes/welcome/page";
import WelcomeStampPage from "../Routes/welcome-stamp/page";
import Community from "../Routes/community/page";
import CommunityDetail from "../Routes/community/detail/page";
import CampaignsListPage from "../Routes/campaigns/page";
import CampaignDetailPage from "../Routes/campaigns/[campaignId]/page";
import CampaignJoinPage from "../Routes/campaigns/[campaignId]/join/page";
import MyBrandLikesPage from "../Routes/campaigns/my-brand-likes/page";
import NiceResultForDepositPage from "../Routes/nice-result-for-deposit/page";
import MyChallengesPage from "../Routes/my-challenges/page";
import ParticipationDetailPage from "../Routes/campaigns/participation/[participationId]/page";
import SubmissionDetailPage from "../Routes/campaigns/submission/[submissionId]/page";

const HomeAuth = ({ children }: { children: JSX.Element }) => {
  const {
    isFirst,
    isAdminLoggedIn,
    isSellerFirst,
    lastPage,
    isManagerLoggedIn,
  } = useAuth();

  if (isFirst && !isWeb()) {
    return <Navigate to="/onboarding/start" />;
  }

  if (isAdminLoggedIn && lastPage === "seller") {
    if (isSellerFirst) {
      return <Navigate to="/boss/onboarding/sales-overview" />;
    }

    return <Navigate to="/boss/home" />;
  }

  if (isManagerLoggedIn && lastPage === "seller") {
    return <Navigate to="/manager/home" />;
  }

  return children;
};

const UserAuth = ({ children }: { children: JSX.Element }) => {
  const { isLoggedIn } = useAuth();

  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

const ManagerAuth = ({ children }: { children: JSX.Element }) => {
  const { isManagerLoggedIn } = useAuth();

  if (!isManagerLoggedIn) {
    return <Navigate to="/manager/login" replace />;
  }
  return children;
};

// prettier-ignore
export const router = createBrowserRouter([
  {
    path: "/",
    element: <Outlet />,
    errorElement: <RouteErrorFallback />,
    children: [
      {
        index: true,
        element: (
          <HomeAuth>
            <Home />
          </HomeAuth>
        ),
      },
      { path: "onboarding/start", element: <OnboardingStart /> },
  { path: "onboarding/price", element: <OnboardingPrice /> },
      { path: "onboarding/friends", element: <OnboardingFriends /> },
      { path: "onboarding/notification/info", element: <OnboardingNotificationInfo /> },
      { path: "onboarding/notification/request", element: <OnboardingNotificationRequest /> },
      { path: "onboarding/welcome", element: <OnboardingWelcome /> },
      { path: "onboarding/location", element: <OnboardingLocation /> },
      { path: "search", element: <Search /> },
      { path: "location", element: <Location /> },
      { path: "like", element: <Like /> },
      { path: "frequency", element: <Frequency /> },
      { path: "event/sns-promotion", element: <SnsPromotion /> },
      { path: "campaigns", element: <CampaignsListPage /> },
      { path: "campaigns/:campaignId", element: <CampaignDetailPage /> },
      {
        path: "campaigns/:campaignId/join",
    element: (
      <UserAuth>
        <CampaignJoinPage />
      </UserAuth>
    ),
  },
      {
        path: "campaigns/my-brand-likes",
    element: (
      <UserAuth>
        <MyBrandLikesPage />
      </UserAuth>
    ),
  },
      {
        path: "nice-result-for-deposit",
    element: (
      <UserAuth>
        <NiceResultForDepositPage />
      </UserAuth>
    ),
  },
      {
        path: "my-challenges",
    element: (
      <UserAuth>
        <MyChallengesPage />
      </UserAuth>
    ),
  },
      {
        path: "campaigns/participation/:participationId",
    element: (
      <UserAuth>
        <ParticipationDetailPage />
      </UserAuth>
    ),
  },
      {
        path: "campaigns/submission/:submissionId",
        element: (
          <UserAuth>
            <SubmissionDetailPage />
          </UserAuth>
        ),
      },
      { path: "user-guide", element: <UserGuide /> },
  { path: "community", element: <Community /> },
      { path: "community/detail/:id", element: <CommunityDetail /> },

      { path: "store/:id", element: <Store /> },
      { path: "store/:id/review", element: <StoreReview /> },
      { path: "product/:id", element: <Product /> },
      { path: "pack/:storeId", element: <Pack /> },

      { path: "live-order/checkout", element: <CheckoutLiveOrder /> },
      {
        path: "live-order/intro/:storeId",
        element: <CheckoutPreOrderIntro isLive />,
      },
      { path: "pre-order/intro/:storeId", element: <CheckoutPreOrderIntro /> },
      { path: "pre-order/checkout/:storeId", element: <CheckoutPreOrder /> },
      { path: "nice-result", element: <NiceResult /> },
      {
        path: "payment/toss/widget",
        element: (
          <UserAuth>
            <TossWidgetCheckoutPage />
          </UserAuth>
        ),
      },
      {
        path: "payment/toss/return",
        element: <TossPaymentReturnPage />,
      },

      { path: "explore", element: <Explore /> },

      { path: "order", element: <Order /> },
      { path: "order/:id", element: <OrderDetail /> },
      { path: "order/:id/review", element: <OrderReview /> },
      { path: "order/writable-review", element: <OrderWritableReview /> },
      { path: "order/review/complete", element: <OrderReviewComplete /> },

      { path: "notification-settings", element: <NotificationSettings /> },

      { path: "my", element: <My /> },
      { path: "my/review", element: <Review /> },
      { path: "my/coupon", element: <Coupon /> },
      { path: "my/coupon/register", element: <CouponRegister /> },
      { path: "my/coupon/register/complete", element: <CouponRegisterComplete /> },
      { path: "my/wallet", element: <Wallet /> },
      { path: "my/invite/intro", element: <InviteIntro /> },
      { path: "my/invite/register", element: <InviteRegister /> },
      { path: "my/invite/register/complete", element: <InviteRegisterComplete /> },
      { path: "my/policy", element: <Policy /> },
      { path: "my/policy/coupon", element: <CouponPolicy /> },
      { path: "my/contact", element: <Contact /> },
      { path: "my/customer-service", element: <CustomerService /> },
      { path: "my/settings", element: <Settings /> },
      { path: "login", element: <Login /> },
      { path: "auth", element: <KakaoRedirectHandler /> },
      { path: "sign-up", element: <SignUp /> },
      { path: "sign-out", element: <SignOut /> },
      {
        path: "change-password",
        element: (
          <UserAuth>
            <ChangePassword />
          </UserAuth>
        ),
      },
      { path: "password-reset", element: <PasswordReset /> },
      { path: "phone-number-auth", element: <PhoneNumberAuthPage /> },
      { path: "phone-number-auth-completed", element: <PhoneNumberAuthCompletedPage /> },

      { path: "welcome", element: <Welcome /> },
      { path: "welcome-stamp", element: <WelcomeStampPage /> },

      { path: "manager/login", element: <ManagerLogin /> },
      {
        path: "manager/home",
        element: (
          <ManagerAuth>
            <ManagerHome />
          </ManagerAuth>
        ),
      },
      ...bossRoutes.map((r) => ({ ...r, path: r.path.replace(/^\//, "") })),
      ...adminRoutes.map((r) => ({ ...r, path: r.path.replace(/^\//, "") })),
      ...missionAdminRoutes.map((r) => ({ ...r, path: r.path.replace(/^\//, "") })),
      ...brandRoutes.map((r) => ({ ...r, path: r.path.replace(/^\//, "") })),
    ],
  },
]);
